import React from 'react';
import { useTranslation } from 'react-i18next';
import { useBridgeState } from '@digitaz/bridge-sdk-client';
import * as S from './DesktopNav.styles';
import {
  DESKTOP_NAV_ITEMS_ORDER_1,
  DESKTOP_NAV_ITEMS_ORDER_2,
} from './DesktopNav.constants';
import { useNavMenuStore } from '@/store/useNavMenuStore';
import { useBreakpoint } from '@/hooks/useBreakpoint';
import { useChangeLang } from '@/components/ChangeLang/useChangeLang';
import { useTheme } from '@/hooks/theme/useTheme';
import NoSsr from '@/components/NoSsr/NoSsr';
import { Portal } from '@/components/UI/Portal/Portal';
import { CommonTestIds } from '@/__e2e__/testIds/common';
import { getNavItemsByOrder } from '@/components/BurgerNav/BurgerNavDrawer.helpers';
import { ChangeLang } from '@/components/ChangeLang/ChangeLang';
import { makeHrefForPhone } from '@/utils/helpers';
import { CONTACT_US } from '@/constants/contactUs';

export const DesktopNav = () => {
  const { isDesktop } = useBreakpoint();
  const { isWebView } = useBridgeState();
  const changeLang = useChangeLang();
  const { t, i18n } = useTranslation();
  const { isDarkTheme } = useTheme();
  const [isNavOpen, toggleNav] = useNavMenuStore(state => [
    state.isNavOpen,
    state.toggleNav,
  ]);

  return (
    <NoSsr>
      <Portal>
        <S.DesktopNavOverlay
          data-testid={CommonTestIds.navMenu.overlay}
          isOpen={isNavOpen}
          onClick={toggleNav}
        />
      </Portal>

      {isNavOpen && (
        <S.DesktopNavWrapper data-testid={CommonTestIds.navMenu.drawer}>
          <S.DesktopNavigation>
            <S.DesktopNavColumns>
              {[DESKTOP_NAV_ITEMS_ORDER_1, DESKTOP_NAV_ITEMS_ORDER_2].map(
                (order, index) => (
                  <S.DesktopNavColumn key={index}>
                    {getNavItemsByOrder(order, toggleNav, i18n, isDesktop).map(
                      ({ id, ...item }) => (
                        <S.DesktopNavItem key={id} {...item} />
                      )
                    )}
                  </S.DesktopNavColumn>
                )
              )}
            </S.DesktopNavColumns>

            <S.DesktopNavColumn $isLeftContent>
              {!isWebView && (
                <S.DesktopNavItem
                  onClick={changeLang}
                  $leftIcon={{
                    name: 'language.svg',
                    width: 19,
                    height: 19,
                  }}
                >
                  <ChangeLang />
                </S.DesktopNavItem>
              )}
              <S.DesktopNavItems>
                <S.ConnectWithUsTitle>
                  {t('common.connect_with_us')}
                </S.ConnectWithUsTitle>
                <S.DesktopNavItem
                  href={makeHrefForPhone(CONTACT_US.PHONE)}
                  as="a"
                  $leftIcon={{
                    name: `phone${isDarkTheme ? '-light' : ''}.svg`,
                    width: 18,
                    height: 18,
                    transition: 'background-color linear 0.1s;',
                  }}
                >
                  {CONTACT_US.PHONE}
                </S.DesktopNavItem>
              </S.DesktopNavItems>
              <S.DesktopNavItem
                href={`mailto:${CONTACT_US.EMAIL}`}
                as="a"
                target="_blank"
              >
                {CONTACT_US.EMAIL}
              </S.DesktopNavItem>
              <S.DesktopNavItems $isRow $gap={25}>
                <S.DesktopNavItem
                  href={CONTACT_US.FACEBOOK}
                  as="a"
                  target="_blank"
                  $leftIcon={{ name: 'fb_grey.svg', width: 20 }}
                />
                <S.DesktopNavItem
                  href={CONTACT_US.INSTAGRAM}
                  as="a"
                  target="_blank"
                  $leftIcon={{ name: 'instagram-alt.svg' }}
                />
              </S.DesktopNavItems>
            </S.DesktopNavColumn>
          </S.DesktopNavigation>
        </S.DesktopNavWrapper>
      )}
    </NoSsr>
  );
};
